import { formatCurrency } from "@/lib/currency";
import { useEffect, useState, useCallback, useRef } from "react";
import { DesktopJobsTable } from "@/components/desktop/DesktopJobsTable";
import { cacheJobsForOffline, getCachedAwaitingDeliveryJobs } from "@/lib/jobCacheHelper";
import { isNative } from "@/lib/capacitor";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { ReleaseKeyButton } from "@/components/ReleaseKeyButton";
import { Calendar, MapPin, Package, AlertCircle, Phone, Navigation, Share2, User, Loader2, XCircle, ArrowLeftRight, Building2, Pencil, Info, CheckCircle2 } from "lucide-react";
import { JobSummaryDialog, type JobSummaryData } from "@/components/JobSummaryDialog";
import { formatAddressShort, openNavigation, joinAddressAndPostcode } from "@/lib/addressFormat";
import { resolveCollectionAddress, resolveCollectionPostcode } from "@/lib/jobAddressHelpers";
import { sendDriverJobNotification } from "@/lib/sendDriverJobNotification";
import { DriverNotesBlock } from "@/components/DriverNotesBlock";
import { NavigationChoiceDialog } from "@/components/NavigationChoiceDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isValidPhoneNumber, formatPhoneToE164 } from "@/lib/phoneUtils";
import { format } from "date-fns";
import { toast } from "sonner";
import { useUserRole } from "@/hooks/useUserRole";
import { useCompany } from "@/hooks/useCompany";
import { useActivityLog } from "@/hooks/useActivityLog";
import { CancelJobModal } from "@/components/CancelJobModal";
import { JobStatusBadge, AppraisalStatusBadge, PartialAppraisalBadge } from "@/components/JobStatusBadge";
import { JobOfflineStatus } from "@/components/JobOfflineStatus";
import { UKRegPlate } from "@/components/UKRegPlate";
import { useIsMobile } from "@/hooks/use-mobile";

interface Job {
  id: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_registration: string;
  vin_number: string | null;
  delivery_address: string;
  delivery_postcode: string | null;
  delivery_contact: string;
  collection_address: string | null;
  collection_postcode: string | null;
  collection_completed_at: string;
  collection_skipped: boolean;
  tracking_shared: boolean;
  is_return_leg: boolean;
  has_return_vehicle: boolean;
  return_vehicle_registration: string | null;
  parent_job_id: string | null;
  driver_id: string | null;
  collection_appraisal_id: string | null;
  estimated_cost?: number | null;
  invoice_amount?: number | null;
  dispatch_notes: string | null;
  release_key_images: any;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  execution_type?: string;
  executing_company_id?: string | null;
  customers: {
    name: string;
    phone: string;
    address?: string;
    postcode?: string;
  } | null;
  drivers: {
    name: string;
  } | null;
}

interface AppraisalInfo {
  hasPhotosOnly: boolean;
  skippedExterior: boolean;
  skippedInterior: boolean;
}

interface Driver {
  id: string;
  name: string;
}

export const AwaitingDeliverySection = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharingJobId, setSharingJobId] = useState<string | null>(null);
  const [appraisalInfoMap, setAppraisalInfoMap] = useState<Record<string, AppraisalInfo>>({});
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelTargetJob, setCancelTargetJob] = useState<Job | null>(null);
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const [navDialogAddress, setNavDialogAddress] = useState("");
  const [navDialogLabel, setNavDialogLabel] = useState("");
  const [summaryJob, setSummaryJob] = useState<JobSummaryData | null>(null);
  const [subNames, setSubNames] = useState<Record<string, string>>({});
  const [subDriverNames, setSubDriverNames] = useState<Record<string, string>>({});
  const [parentJobInfo, setParentJobInfo] = useState<Record<string, { vehicle_make: string; vehicle_model: string; vehicle_registration: string; collection_address: string | null; delivery_address: string }>>({});
  const fetchInFlightRef = useRef(false);
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const { companyId } = useCompany();
  const { logActivity } = useActivityLog();
  const isMobile = useIsMobile();

  const safeFormatDate = (value: string | null | undefined, pattern: string, fallback = "—") => {
    if (!value) return fallback;
    try {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return fallback;
      return format(parsed, pattern);
    } catch {
      return fallback;
    }
  };

  const generateTrackingLink = (jobId: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/track?jobId=${jobId}`;
  };

  // Get GPS coordinates with retry logic (up to 3 attempts)
  const getLocationWithRetry = useCallback(async (maxAttempts = 3): Promise<{ latitude: number; longitude: number } | null> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`[ShareTracking] Location attempt ${attempt}/${maxAttempts}`);
        let latitude: number;
        let longitude: number;

        if (isNative) {
          const { Geolocation } = await import("@capacitor/geolocation");
          const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: attempt <= 2, // Fall back to low accuracy on last attempt
            timeout: attempt === 1 ? 8000 : 12000, // Longer timeout on retries
          });
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
        } else {
          if (!navigator.geolocation) {
            toast.error("Geolocation not supported", {
              description: "Your browser doesn't support location services"
            });
            return null; // No point retrying
          }

          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            const timeoutMs = attempt === 1 ? 8000 : 12000;
            const timeoutId = setTimeout(() => reject(new Error("timeout")), timeoutMs);
            navigator.geolocation.getCurrentPosition(
              (pos) => { clearTimeout(timeoutId); resolve(pos); },
              (err) => { clearTimeout(timeoutId); reject(err); },
              { enableHighAccuracy: attempt <= 2, timeout: timeoutMs - 1000, maximumAge: 0 }
            );
          });
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
        }

        console.log(`[ShareTracking] Location obtained on attempt ${attempt}:`, latitude, longitude);
        return { latitude, longitude };
      } catch (error: any) {
        console.warn(`[ShareTracking] Attempt ${attempt} failed:`, error?.message || error);

        // Permission denied — don't retry, it won't change
        if (error?.code === 1 || error?.message?.includes("permission") || error?.message?.includes("denied")) {
          toast.error("Location Error", {
            description: "Location permission denied. Please enable location access in your device Settings.",
            duration: 5000
          });
          return null;
        }

        // Last attempt — show error
        if (attempt === maxAttempts) {
          const errorMessage = error?.message === "timeout"
            ? "Location request timed out after multiple attempts. Please check GPS is enabled."
            : "Unable to get your location after multiple attempts. Please try again.";
          toast.error("Location Error", { description: errorMessage, duration: 5000 });
          return null;
        }

        // Wait briefly before retrying
        await new Promise(r => setTimeout(r, 1000));
      }
    }
    return null;
  }, [isNative]);

  // Function to get fresh location and update driver's position
  const refreshDriverLocation = useCallback(async (): Promise<boolean> => {
    try {
      const coords = await getLocationWithRetry(3);
      if (!coords) return false;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('[ShareTracking] No authenticated user');
        return false;
      }

      const { data: driver } = await supabase
        .from("drivers")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!driver) {
        console.error('[ShareTracking] No driver record found');
        return false;
      }

      const { error: updateError } = await supabase
        .from("drivers")
        .update({
          latitude: coords.latitude,
          longitude: coords.longitude,
          last_location_update: new Date().toISOString(),
          is_active: true,
        })
        .eq("id", driver.id);

      if (updateError) {
        console.error('[ShareTracking] Failed to update location:', updateError);
        return false;
      }

      console.log('[ShareTracking] Driver location updated successfully');
      return true;
    } catch (error: any) {
      console.error('[ShareTracking] Unexpected error:', error);
      toast.error("Location Error", { description: "An unexpected error occurred. Please try again.", duration: 5000 });
      return false;
    }
  }, [getLocationWithRetry]);

  const handleShareTracking = async (jobId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSharingJobId(jobId);
    
    try {
      // Step 1: Request fresh location permission and get current coordinates
      toast.info("Getting your current location...", { duration: 2000 });
      
      const locationUpdated = await refreshDriverLocation();
      
      if (!locationUpdated) {
        toast.error("Could not update location", {
          description: "Please enable location access and try again"
        });
        setSharingJobId(null);
        return;
      }

      // Step 2: Send the tracking SMS with fresh location
      console.log(`[ShareTracking] Invoking send-tracking-sms for job ${jobId}`);
      
      let data: any = null;
      let invokeError: any = null;
      
      try {
        // Always use direct fetch with explicit auth headers to avoid
        // CapacitorHttp / supabase SDK token issues on native
        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;
        if (!accessToken) throw new Error('No auth session — please sign in again');
        
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        
        const response = await fetch(`${supabaseUrl}/functions/v1/send-tracking-sms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'apikey': anonKey,
          },
          body: JSON.stringify({ jobId }),
        });
        
        data = await response.json();
        if (!response.ok) {
          invokeError = { message: data?.error || `HTTP ${response.status}` };
        }
      } catch (err: any) {
        console.error('[ShareTracking] send-tracking-sms failed:', err);
        invokeError = { message: err?.message || 'Network request failed' };
      }
      
      // Check for errors in the invoke response
      if (invokeError) {
        console.error('[ShareTracking] Edge function error:', invokeError);
        toast.error("Failed to send tracking SMS", {
          description: typeof invokeError === 'string' ? invokeError : (invokeError?.message || "Please try again")
        });
        setSharingJobId(null);
        return;
      }
      
      // Also check if data itself contains an error (edge function returned 200 with error body)
      if (data && typeof data === 'object' && data.error && !data.success) {
        console.error('[ShareTracking] Edge function returned error in body:', data.error);
        toast.error("Failed to send tracking SMS", {
          description: data.error
        });
        setSharingJobId(null);
        return;
      }

      console.log('[ShareTracking] SMS sent successfully:', data);
      toast.success("Tracking SMS sent!", {
        description: "Customer will receive location updates and a 10-minute arrival alert"
      });

      // Log tracking SMS activity
      const job = jobs.find(j => j.id === jobId);
      await logActivity({
        actionType: "tracking_sms_sent",
        description: `Tracking SMS sent for ${job?.vehicle_make || ''} ${job?.vehicle_model || ''} (${job?.vehicle_registration || ''})`,
        metadata: { jobId, vehicle: `${job?.vehicle_make || ''} ${job?.vehicle_model || ''}`, registration: job?.vehicle_registration || '' },
      });
      
      // Refresh the jobs list to update the UI
      fetchAwaitingDeliveryJobs();
    } catch (error: any) {
      console.error('[ShareTracking] Unexpected error:', error);
      toast.error("Failed to send tracking SMS", {
        description: error?.message || "An unexpected error occurred. Please try again."
      });
    } finally {
      setSharingJobId(null);
    }
  };

  // ── SINGLE combined effect for load + realtime, debounced ──
  useEffect(() => {
    if (roleLoading || !companyId) return;

    if (isNative) {
      getCachedAwaitingDeliveryJobs(companyId).then(cached => {
        if (cached.length > 0) {
          setJobs(cached as any);
          setLoading(false);
        }
      }).catch((e) => {
        console.warn("[AwaitingDelivery] IndexedDB cache read failed:", e);
      });
    }

    fetchAwaitingDeliveryJobs();
    fetchDrivers();

    let channel: ReturnType<typeof supabase.channel> | null = null;
    if (companyId) {
      const debouncedRefetch = () => {
        if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
        realtimeDebounceRef.current = setTimeout(() => fetchAwaitingDeliveryJobs(), 500);
      };

      try {
        channel = supabase
          .channel('awaiting-delivery-jobs')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs', filter: `company_id=eq.${companyId}` }, debouncedRefetch)
          .subscribe();
      } catch (e) {
        console.error("[AwaitingDelivery] Realtime subscription error:", e);
      }

      // Refetch on app resume / tab focus to clear stale cards
      const handleVisibility = () => {
        if (document.visibilityState === "visible") debouncedRefetch();
      };
      document.addEventListener("visibilitychange", handleVisibility);

      return () => {
        if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
        if (channel) supabase.removeChannel(channel);
        document.removeEventListener("visibilitychange", handleVisibility);
      };
    }
  }, [companyId, isAdmin, roleLoading]);

  // ── Fetch with concurrent-request guard ──
  const fetchAwaitingDeliveryJobs = async () => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (!companyId) return;
      const query = isAdmin
        ? supabase
            .from('jobs')
            .select(`
              id,
              vehicle_make,
              vehicle_model,
              vehicle_registration,
              vin_number,
              collection_address,
              collection_postcode,
              delivery_address,
              delivery_postcode,
              delivery_contact,
              collection_completed_at,
              collection_skipped,
              tracking_shared,
              is_return_leg,
              has_return_vehicle,
              return_vehicle_registration,
              parent_job_id,
              driver_id,
              collection_appraisal_id,
              estimated_cost,
              invoice_amount,
              dispatch_notes,
              release_key_images,
              scheduled_date,
              scheduled_time,
              execution_type,
              executing_company_id,
              customers (
                name,
                phone,
                address,
                postcode
              ),
              drivers (
                name
              )
            `)
        : supabase
            .from('jobs')
            .select(`
              id,
              vehicle_make,
              vehicle_model,
              vehicle_registration,
              vin_number,
              collection_address,
              collection_postcode,
              delivery_address,
              delivery_postcode,
              delivery_contact,
              collection_completed_at,
              collection_skipped,
              tracking_shared,
              is_return_leg,
              has_return_vehicle,
              return_vehicle_registration,
              parent_job_id,
              driver_id,
              collection_appraisal_id,
              dispatch_notes,
              release_key_images,
              scheduled_date,
              scheduled_time,
              execution_type,
              executing_company_id,
              customers (
                name,
                phone,
                address,
                postcode
              ),
              drivers (
                name
              )
            `);

      const { data, error } = await query
        .is('delivery_completed_at', null)
        .is('completed_at', null)
        .or([
          'status.eq.awaiting_delivery',
          'status.eq.in_progress',
          'and(status.eq.pending,collection_completed_at.not.is.null)',
          'and(status.eq.pending,collection_skipped.eq.true)',
        ].join(','))
        .order('scheduled_date', { ascending: true, nullsFirst: false })
        .order('scheduled_time', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Show jobs IMMEDIATELY — don't wait for sub-queries
      setJobs(data || []);
      setLoading(false);
      
      // Cache jobs to IndexedDB for offline access on native
      cacheJobsForOffline(data || []);

      // Resolve supplementary data in background (non-blocking)
      resolveSupplementaryData(data || []);
    } catch (error) {
      console.error('Error fetching awaiting delivery jobs:', error);
      // On native, fall back to cached jobs when offline
      if (isNative) {
        try {
          const cached = await getCachedAwaitingDeliveryJobs(companyId);
          if (cached.length > 0) {
            console.log(`[AwaitingDelivery] Offline fallback: ${cached.length} cached jobs`);
            setJobs(cached as any);
          }
        } catch (cacheErr) {
          console.warn("[AwaitingDelivery] Cache fallback failed:", cacheErr);
        }
      }
    } finally {
      setLoading(false);
      fetchInFlightRef.current = false;
    }
  };

  /** Resolve subcontractor names, driver names, parent job info, and appraisal info without blocking UI */
  const resolveSupplementaryData = async (data: any[]) => {
    try {
      // Run all supplementary queries in parallel
      await Promise.allSettled([
        resolveSubcontractorNames(data),
        resolveParentJobInfo(data),
        data.length > 0 ? fetchAppraisalInfo(data) : Promise.resolve(),
      ]);
    } catch (err) {
      console.warn("[AwaitingDelivery] Supplementary data fetch failed (non-fatal):", err);
    }
  };

  const resolveSubcontractorNames = async (data: any[]) => {
    // Resolve subcontractor company names
    const subCompanyIds = [...new Set(
      data
        .filter((j: any) => j.execution_type === 'subcontracted' && j.executing_company_id)
        .map((j: any) => j.executing_company_id as string)
    )];
    if (subCompanyIds.length > 0) {
      const nameMap: Record<string, string> = {};
      await Promise.all(subCompanyIds.map(async (cid) => {
        try {
          const { data: info } = await supabase.rpc("get_linked_company_info", { p_company_id: cid });
          const row = Array.isArray(info) ? info[0] : info;
          if (row?.company_name) nameMap[cid] = row.company_name;
        } catch {}
      }));
      setSubNames(prev => ({ ...prev, ...nameMap }));
    }

    // Resolve driver names for subcontracted jobs where RLS hides the driver join
    const subDriverJobs = data.filter(
      (j: any) => j.execution_type === 'subcontracted' && j.driver_id && !j.drivers
    );
    if (subDriverJobs.length > 0) {
      const driverNameMap: Record<string, string> = {};
      for (const job of subDriverJobs) {
        try {
          const { data: info } = await supabase.rpc("get_linked_company_info", { p_company_id: job.executing_company_id });
          const row = Array.isArray(info) ? info[0] : info;
          const companyName = row?.company_name || 'Subcontractor';
          driverNameMap[job.driver_id] = companyName + ' Driver';
        } catch {
          driverNameMap[job.driver_id] = 'Subcontractor Driver';
        }
      }
      setSubDriverNames(prev => ({ ...prev, ...driverNameMap }));
    }
  };

  const resolveParentJobInfo = async (data: any[]) => {
    const returnLegJobs = data.filter((j: any) => j.is_return_leg && j.parent_job_id);
    if (returnLegJobs.length > 0) {
      const parentIds = [...new Set(returnLegJobs.map((j: any) => j.parent_job_id as string))];
      const { data: parentJobs } = await supabase
        .from('jobs')
        .select('id, vehicle_make, vehicle_model, vehicle_registration, collection_address, delivery_address')
        .in('id', parentIds);
      if (parentJobs) {
        const infoMap: Record<string, any> = {};
        parentJobs.forEach((p: any) => { infoMap[p.id] = p; });
        setParentJobInfo(prev => ({ ...prev, ...infoMap }));
      }
    }
  };

  const fetchAppraisalInfo = async (jobsData: Job[]) => {
    const infoMap: Record<string, AppraisalInfo> = {};
    
    const exteriorParts = [
      "Front Bumper", "Bonnet/Hood", "Front Left Wing", "Front Left Wheel (NSF)",
      "Front Left Door", "Rear Left Door", "Rear Left Wheel (NSR)", "Rear Left Quarter Panel",
      "Rear Bumper", "Boot/Trunk", "Rear Window", "Rear Right Quarter Panel",
      "Rear Right Wheel (OSR)", "Rear Right Door", "Front Right Door", "Front Right Wheel (OSF)",
      "Front Right Wing", "Windscreen/Windshield", "Roof"
    ];
    const interiorParts = [
      "Front Seats (Driver)", "Dashboard", "Steering Wheel", "Center Console",
      "Door Panels (Front Right)", "Door Panels (Rear Right)", "Rear Seats",
      "Boot/Trunk Interior", "Door Panels (Rear Left)", "Door Panels (Front Left)",
      "Front Seats (Passenger)", "Headliner/Roof Lining", "Carpets/Floor Mats"
    ];
    
    // Batch: collect all appraisal IDs and fetch inspections in ONE query
    const appraisalIds = jobsData
      .filter(j => j.collection_appraisal_id)
      .map(j => j.collection_appraisal_id!);
    
    let inspectionsByAppraisal: Record<string, string[]> = {};
    if (appraisalIds.length > 0) {
      try {
        const { data: allInspections } = await supabase
          .from('vehicle_part_inspections')
          .select('appraisal_id, part_name')
          .in('appraisal_id', appraisalIds);
        
        if (allInspections) {
          for (const insp of allInspections) {
            if (!inspectionsByAppraisal[insp.appraisal_id]) inspectionsByAppraisal[insp.appraisal_id] = [];
            inspectionsByAppraisal[insp.appraisal_id].push(insp.part_name);
          }
        }
      } catch (err) {
        console.warn("[AwaitingDelivery] Batch inspection fetch failed:", err);
      }
    }
    
    for (const job of jobsData) {
      if (!job.collection_appraisal_id) {
        infoMap[job.id] = { hasPhotosOnly: false, skippedExterior: false, skippedInterior: false };
        continue;
      }
      
      const partNames = inspectionsByAppraisal[job.collection_appraisal_id] || [];
      if (partNames.length === 0) {
        infoMap[job.id] = { hasPhotosOnly: false, skippedExterior: false, skippedInterior: false };
        continue;
      }
      
      const hasVehiclePhotos = partNames.includes("Vehicle Photos");
      const hasExteriorParts = partNames.some(p => exteriorParts.includes(p));
      const hasInteriorParts = partNames.some(p => interiorParts.includes(p));
      
      infoMap[job.id] = {
        hasPhotosOnly: hasVehiclePhotos && !hasExteriorParts && !hasInteriorParts,
        skippedExterior: !hasExteriorParts && hasVehiclePhotos,
        skippedInterior: hasExteriorParts && !hasInteriorParts
      };
    }
    
    setAppraisalInfoMap(infoMap);
  };

  const fetchDrivers = async () => {
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('id, name')
        .order('name', { ascending: true });

      if (error) throw error;
      setDrivers(data || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const handleReschedule = async (jobId: string, date: string, time: string | null) => {
    try {
      const updateData: any = { scheduled_date: date, scheduled_time: time };
      const { data: updatedJob, error } = await supabase
        .from('jobs')
        .update(updateData)
        .eq('id', jobId)
        .select('driver_id, execution_type, executing_company_id, company_id')
        .single();
      if (error) throw error;
      toast.success("Schedule updated");
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, scheduled_date: date, scheduled_time: time } : j));

      // Use updatedJob from DB response, or fall back to local state
      const localJob = jobs.find(j => j.id === jobId);
      const driverId = updatedJob?.driver_id || localJob?.driver_id;
      const executionType = updatedJob?.execution_type || localJob?.execution_type;
      const executingCompanyId = updatedJob?.executing_company_id || localJob?.executing_company_id;
      const companyId = updatedJob?.company_id;

      console.log('[Reschedule] Job', jobId, '- driverId:', driverId, '- updatedJob:', JSON.stringify(updatedJob), '- localJob driver_id:', localJob?.driver_id);

      // Notify the assigned driver about the schedule change
      if (driverId) {
        try {
          console.log('[Reschedule] Sending job_updated notification for job', jobId, 'driver', driverId);
          const { data: notifyResult, error: notifyError } = await supabase.functions.invoke('notify-driver-job-assigned', {
            body: { job_id: jobId, driver_id: driverId, notification_type: 'job_updated' },
          });
          console.log('[Reschedule] Notification result:', JSON.stringify(notifyResult), 'error:', notifyError);
        } catch (notifyErr) {
          console.error('[Reschedule] Failed to send job update notification:', notifyErr);
        }
      } else {
        console.log('[Reschedule] No driver assigned to job', jobId, '- skipping notification');
      }

      // Also notify subcontractor if job is subcontracted
      if (executingCompanyId && executionType === 'subcontracted') {
        try {
          await supabase.functions.invoke('notify-subcontractor-job-allocated', {
            body: { job_id: jobId, executing_company_id: executingCompanyId, owner_company_id: companyId, notification_type: 'job_updated' },
          });
        } catch (subErr) {
          console.error('[Reschedule] Failed to notify subcontractor:', subErr);
        }
      }
    } catch (error: any) {
      console.error('Error updating schedule:', error);
      toast.error("Failed to update schedule");
    }
  };

  const handleAssignDriver = async (jobId: string, driverId: string) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ driver_id: driverId })
        .eq('id', jobId);

      if (error) throw error;

      // Send push notification to assigned driver
      try {
        await supabase.functions.invoke('notify-driver-job-assigned', {
          body: {
            job_id: jobId,
            driver_id: driverId,
            notification_type: 'job_assigned'
          }
        });
      } catch (notifyError) {
        console.error('Failed to send driver notification:', notifyError);
      }

      toast.success("Driver assigned successfully");
      fetchAwaitingDeliveryJobs();
    } catch (error) {
      console.error('Error assigning driver:', error);
      toast.error("Failed to assign driver");
    }
  };

  if (loading) {
    return (
      <Card className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <Package className="w-5 h-5" />
            Awaiting Delivery
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  if (jobs.length === 0) {
    return null;
  }

  const isDesktop = !isMobile && !isNative;

  return (
    <Card className={isDesktop ? "border-border/50 bg-card/80 backdrop-blur" : "border-amber-500/50 bg-gradient-to-br from-amber-50/80 to-amber-100/50 dark:from-amber-950/20 dark:to-amber-900/10"}>
      <CardHeader className="p-3 sm:p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            {!isDesktop && (
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            )}
            <div>
              <CardTitle className={isDesktop ? "flex items-center gap-2 text-base sm:text-lg md:text-xl" : "text-amber-700 dark:text-amber-400 text-base sm:text-lg md:text-xl"}>
                {isDesktop && <Package className="w-4 h-4 sm:w-5 sm:h-5" />}
                Awaiting Delivery
                <Badge variant="secondary" className="text-xs">{jobs.length}</Badge>
              </CardTitle>
              <CardDescription className={isDesktop ? "text-xs sm:text-sm" : "text-amber-600 dark:text-amber-500 text-xs sm:text-sm"}>
                {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'} ready for delivery
              </CardDescription>
            </div>
          </div>
          <Badge variant="secondary" className={isDesktop ? "self-start sm:self-auto text-xs sm:text-sm" : "bg-amber-500 text-white hover:bg-amber-600 self-start sm:self-auto text-xs sm:text-sm"}>
            {jobs.length} PoD Pending
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
        {/* Desktop Table View */}
        {!isMobile && !isNative && isAdmin ? (
          <DesktopJobsTable
            jobs={jobs}
            drivers={drivers}
            isAdmin={isAdmin}
            variant="awaiting_delivery"
            subNames={subNames}
            subDriverNames={subDriverNames}
            onAssignDriver={handleAssignDriver}
            onReschedule={handleReschedule}
            onEdit={(jobId) => navigate(`/create-job?jobId=${jobId}`)}
            onCancel={(job) => { setCancelTargetJob(job); setCancelModalOpen(true); }}
          />
        ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <div 
              key={job.id} 
              className="flex flex-col p-4 sm:p-5 rounded-xl border border-amber-200 dark:border-amber-800 bg-white dark:bg-amber-950/30 hover:shadow-md transition-all cursor-pointer"
            >
              {/* === SECTION 1: Job Identity === */}
              <div onClick={() => navigate(`/resume-job/${job.id}`)}>
                {/* Header: Vehicle + Status */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <h3 className="native-card-title font-extrabold text-sm sm:text-base text-amber-900 dark:text-amber-100 leading-snug tracking-tight">
                      {job.is_return_leg ? `Return Vehicle – ${(!job.vehicle_registration || job.vehicle_registration === 'TBD' || job.vehicle_registration === 'N/A') && job.vin_number ? job.vin_number : job.vehicle_registration}` : `${job.vehicle_make} ${job.vehicle_model}`}
                    </h3>
                    {isNative && (
                      <UKRegPlate
                        registration={(!job.vehicle_registration || job.vehicle_registration === 'TBD' || job.vehicle_registration === 'N/A') && job.vin_number ? job.vin_number : job.vehicle_registration}
                        className="shrink-0"
                      />
                    )}
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                    <JobStatusBadge 
                      status="in_progress"
                      collectionCompleted={true}
                      collectionSkipped={job.collection_skipped}
                      size="sm"
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSummaryJob(job as unknown as JobSummaryData); }}
                      className="w-7 h-7 rounded-full border border-border/60 bg-muted/50 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      aria-label="View job summary"
                    >
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Second row: Reg plate + badges */}
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {!isNative && <UKRegPlate registration={(!job.vehicle_registration || job.vehicle_registration === 'TBD' || job.vehicle_registration === 'N/A') && job.vin_number ? job.vin_number : job.vehicle_registration} />}
                  {job.is_return_leg && (
                    <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20 text-[10px]">
                      <ArrowLeftRight className="w-3 h-3 mr-0.5" />
                      RETURN
                    </Badge>
                  )}
                  {job.execution_type === 'subcontracted' && (
                    <Badge variant="outline" className="border-primary/50 text-primary text-[10px]">
                      <Building2 className="w-3 h-3 mr-0.5" />
                      {job.executing_company_id && subNames[job.executing_company_id]
                        ? subNames[job.executing_company_id]
                        : 'Subcontracted'}
                    </Badge>
                  )}
                  {job.collection_skipped && (
                    <Badge variant="destructive" className="bg-red-100 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-400 dark:border-red-800 text-[10px]">
                      <AlertCircle className="w-3 h-3 mr-0.5" />
                      PoC Skipped
                    </Badge>
                  )}
                </div>

                {/* Meta rows */}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <AppraisalStatusBadge 
                    type="POC" 
                    completed={!job.collection_skipped}
                    skipped={job.collection_skipped}
                  />
                  {appraisalInfoMap[job.id] && (
                    <PartialAppraisalBadge 
                      hasPhotosOnly={appraisalInfoMap[job.id].hasPhotosOnly}
                      skippedExterior={appraisalInfoMap[job.id].skippedExterior}
                      skippedInterior={appraisalInfoMap[job.id].skippedInterior}
                    />
                  )}
                  <AppraisalStatusBadge 
                    type="POD" 
                    completed={false}
                  />
                  {job.customers?.name && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Package className="w-3 h-3 text-amber-600/60" />
                      {job.customers.name}
                    </span>
                  )}
                  {(() => {
                    const contactName = job.delivery_contact?.split(/[-–]?\s*\+?\d{10,}/)[0]?.trim()?.replace(/[-–]\s*$/, '')?.trim();
                    return contactName && contactName !== job.customers?.name ? (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <User className="w-3 h-3 text-amber-600/60" />
                        {contactName}
                      </span>
                    ) : null;
                  })()}
                </div>

                {/* --- Section divider --- */}
                <div className="my-3 border-t border-amber-200/30 dark:border-amber-800/20" />

                {/* === SECTION 2: Address Block === */}
                {(() => {
                  // SINGLE OWNER: resolveCollectionAddress (jobAddressHelpers.ts)
                  const collectAddr = resolveCollectionAddress(job);
                  const collectPostcode = resolveCollectionPostcode(job);

                  return (
                    <div className="rounded-lg bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30 p-3 space-y-0">
                      {collectAddr && (
                        <>
                          {/* Collection */}
                          <div className="flex gap-2.5">
                            <div className="w-0.5 rounded-full bg-amber-500/60 flex-shrink-0 self-stretch" />
                            <div className="py-1">
                              <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-0.5">Collection</p>
                              <p className="text-xs sm:text-sm text-foreground break-words leading-snug">
                                {formatAddressShort(collectAddr, collectPostcode)}
                              </p>
                            </div>
                          </div>
                          <div className="border-t border-amber-200/30 dark:border-amber-800/15 my-2" />
                        </>
                      )}
                      {/* Delivery */}
                      <div className="flex gap-2.5">
                        <div className="w-0.5 rounded-full bg-green-500/60 flex-shrink-0 self-stretch" />
                        <div className="py-1">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-green-600 dark:text-green-400 mb-0.5">Delivery</p>
                          <p className="text-xs sm:text-sm text-foreground break-words leading-snug">
                            {formatAddressShort(job.delivery_address, job.delivery_postcode)}
                          </p>
                        </div>
                      </div>
                      {/* Driver Notes directly under Delivery */}
                      {job.dispatch_notes && (
                        <>
                          <div className="border-t border-amber-200/30 dark:border-amber-800/15 my-2" />
                          <DriverNotesBlock notes={job.dispatch_notes} />
                        </>
                      )}

                      {/* Release Key Button */}
                      {Array.isArray(job.release_key_images) && job.release_key_images.length > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <ReleaseKeyButton images={job.release_key_images as string[]} />
                          <span className="text-[10px] text-muted-foreground">Release Key</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* --- Section divider --- */}
                <div className="my-3 border-t border-amber-200/30 dark:border-amber-800/20" />

                {/* === SECTION 3: Execution Details === */}
                <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                      <Calendar className="w-3.5 h-3.5" />
                      {job.collection_skipped 
                        ? 'Collection Skipped' 
                        : `Collected: ${safeFormatDate(job.collection_completed_at, 'PP', 'Unknown date')} at ${safeFormatDate(job.collection_completed_at, 'HH:mm', 'Unknown time')}`
                      }
                  </span>
                  {isAdmin && (job.estimated_cost || job.invoice_amount) && (
                    <span className="text-sm sm:text-base font-extrabold text-foreground">
                      {formatCurrency(((job.invoice_amount ?? job.estimated_cost) || 0).toFixed(2))}
                    </span>
                  )}
                </div>

                {/* Return vehicle */}
                {job.has_return_vehicle && !job.is_return_leg && (
                  <div className="flex items-center gap-2 text-xs mt-2 px-2.5 py-2 rounded-lg bg-accent/10 border border-accent/20">
                    <ArrowLeftRight className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                    <span className="text-accent font-medium">Linked Return: {job.return_vehicle_registration || "N/A"}</span>
                  </div>
                )}
                {job.is_return_leg && job.parent_job_id && (
                  <div className="mt-2 px-2.5 py-2 rounded-lg bg-accent/10 border border-accent/20">
                    <div className="flex items-center gap-2 text-xs">
                      <ArrowLeftRight className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                      <span className="text-accent font-medium">Return Leg of Outbound Job</span>
                    </div>
                    {parentJobInfo[job.parent_job_id] && (
                      <div className="mt-1.5 pl-6 text-xs text-muted-foreground space-y-0.5">
                        <p className="font-medium text-foreground">
                          🚗 {parentJobInfo[job.parent_job_id].vehicle_make} {parentJobInfo[job.parent_job_id].vehicle_model} — {parentJobInfo[job.parent_job_id].vehicle_registration}
                        </p>
                        <p>{parentJobInfo[job.parent_job_id].collection_address || 'N/A'} → {parentJobInfo[job.parent_job_id].delivery_address}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Driver badge */}
                {job.drivers && (
                  <div className="mt-2">
                    <Badge variant="secondary" className="text-xs font-normal text-muted-foreground">
                      Driver: {job.drivers.name}
                    </Badge>
                  </div>
                )}
                <div className="mt-1">
                  <JobOfflineStatus jobId={job.id} compact />
                </div>
              </div>

              {/* Driver assignment - admin only */}
              {isAdmin && !(drivers.length === 1 && job.drivers && job.driver_id === drivers[0]?.id) && (
                <div className="mt-3 p-3 bg-muted/30 rounded-lg border" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-primary" />
                    <label className="text-sm font-medium">Driver Assignment</label>
                  </div>
                  {job.drivers ? (
                    <div className="space-y-2">
                      <Badge className="bg-green-500 text-white">✓ Assigned to: {job.drivers.name}</Badge>
                      {drivers.length > 1 && (
                        <Select value={job.driver_id || ""} onValueChange={(value) => handleAssignDriver(job.id, value)}>
                          <SelectTrigger className="h-9 text-sm bg-background border-border">
                            <SelectValue placeholder="Change driver if needed..." />
                          </SelectTrigger>
                          <SelectContent className="z-[200] bg-popover border-border">
                            {drivers.map((driver) => (
                              <SelectItem key={driver.id} value={driver.id} className="bg-popover hover:bg-accent">{driver.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ) : job.execution_type === 'subcontracted' && job.driver_id ? (
                    <div className="space-y-2">
                      <Badge className="bg-blue-500 text-white">
                        ✓ {subDriverNames[job.driver_id] || 'Subcontractor Driver'}
                      </Badge>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Badge variant="destructive" className="mb-1">⚠ No Driver Assigned</Badge>
                      <Select value={job.driver_id || ""} onValueChange={(value) => handleAssignDriver(job.id, value)}>
                        <SelectTrigger className="h-9 text-sm bg-background border-border">
                          <SelectValue placeholder="Select a driver..." />
                        </SelectTrigger>
                        <SelectContent className="z-[200] bg-popover border-border">
                          {drivers.map((driver) => (
                            <SelectItem key={driver.id} value={driver.id} className="bg-popover hover:bg-accent">{driver.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* --- Section divider --- */}
              <div className="my-3 border-t border-amber-200/30 dark:border-amber-800/20" />

              {/* === SECTION 4: Actions === */}
              <div className="flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
                {isMobile && (
                  <Button
                    size="lg"
                    className="h-12 w-full text-base font-semibold"
                    onClick={(e) => { e.stopPropagation(); navigate(`/resume-job/${job.id}`); }}
                  >
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                    Complete Delivery
                  </Button>
                )}
                <div className="flex flex-col gap-2 md:hidden">
                  {(() => {
                    const deliveryPhone = job.delivery_contact;
                    const phoneFromDelivery = deliveryPhone ? deliveryPhone.match(/[\d\s+()-]{10,}/)?.[0]?.trim() : null;
                    const hasValidPhone = isValidPhoneNumber(phoneFromDelivery);
                    const isSharing = sharingJobId === job.id;
                    const isDisabled = !hasValidPhone || isSharing;
                    
                    const button = (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className={`w-full h-10 border-green-500 text-green-700 hover:bg-green-50 dark:hover:bg-green-950/50 ${!hasValidPhone ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={(e) => { e.stopPropagation(); if (!isDisabled) handleShareTracking(job.id, e); }}
                        disabled={isDisabled}
                      >
                        {isSharing ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Getting Location...</>
                        ) : (
                          <><Share2 className="w-4 h-4 mr-2" />{job.tracking_shared ? "Re-share Tracking" : "Share Tracking"}</>
                        )}
                      </Button>
                    );
                    
                    if (!hasValidPhone) {
                      return (
                        <TooltipProvider><Tooltip><TooltipTrigger asChild><span>{button}</span></TooltipTrigger>
                        <TooltipContent><p>No valid phone number available. Add a delivery contact phone to enable SMS tracking.</p></TooltipContent>
                        </Tooltip></TooltipProvider>
                      );
                    }
                    return button;
                  })()}
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="h-12 w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      const addr = joinAddressAndPostcode(job.delivery_address, job.delivery_postcode);
                      setNavDialogAddress(addr);
                      setNavDialogLabel("Navigate to Delivery");
                      setNavDialogOpen(true);
                    }}
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Navigate to Delivery
                  </Button>
                  <div className="flex flex-col gap-2">
                    {(() => {
                      const collectionPhone = job.customers?.phone;
                      const phoneFromCollection = collectionPhone ? collectionPhone.match(/[\d\s+()-]{10,}/)?.[0]?.trim() : null;
                      const collectionPhoneToCall = isValidPhoneNumber(phoneFromCollection) ? phoneFromCollection : null;
                      
                      if (!collectionPhoneToCall) {
                        return (
                          <Button size="sm" variant="outline" className="w-full h-10 text-xs border-amber-500 text-amber-700 opacity-50 cursor-not-allowed" disabled>
                            <Phone className="w-4 h-4 mr-1" />Call Collection
                          </Button>
                        );
                      }
                      const formattedPhone = formatPhoneToE164(collectionPhoneToCall);
                      return (
                        <Button 
                          size="sm" variant="outline"
                          className="w-full h-10 text-xs border-amber-500 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/50"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`tel:${formattedPhone}`, "_system");
                          }}
                        >
                          <Phone className="w-4 h-4 mr-1" />Call Collection
                        </Button>
                      );
                    })()}
                    {(() => {
                      const deliveryPhone = job.delivery_contact;
                      const phoneFromDelivery = deliveryPhone ? deliveryPhone.match(/[\d\s+()-]{10,}/)?.[0]?.trim() : null;
                      const phoneToCall = isValidPhoneNumber(phoneFromDelivery) ? phoneFromDelivery : null;
                      
                      if (!phoneToCall) {
                        return (
                          <Button size="sm" variant="outline" className="w-full h-10 text-xs border-amber-500 text-amber-700 opacity-50 cursor-not-allowed" disabled>
                            <Phone className="w-4 h-4 mr-1" />Call Delivery
                          </Button>
                        );
                      }
                      const formattedPhone = formatPhoneToE164(phoneToCall);
                      return (
                        <Button 
                          size="sm" variant="outline"
                          className="w-full h-10 text-xs border-amber-500 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/50"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`tel:${formattedPhone}`, "_system");
                          }}
                        >
                          <Phone className="w-4 h-4 mr-1" />Call Delivery
                        </Button>
                      );
                    })()}
                  </div>
                </div>
                
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="flex-1 border-amber-500 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/50 h-9 text-xs"
                      onClick={(e) => { e.stopPropagation(); navigate(`/create-job?jobId=${job.id}`); }}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit Job
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="flex-1 text-destructive border-destructive/50 hover:bg-destructive/10 h-9 text-xs"
                      onClick={(e) => { e.stopPropagation(); setCancelTargetJob(job); setCancelModalOpen(true); }}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Cancel Job
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        )}
      </CardContent>

      {/* Cancel Job Modal */}
      {cancelTargetJob && (
        <CancelJobModal
          open={cancelModalOpen}
          onOpenChange={(val) => {
            setCancelModalOpen(val);
            if (!val) setCancelTargetJob(null);
          }}
          jobId={cancelTargetJob.id}
          vehicleReg={cancelTargetJob.vehicle_registration}
          defaultAbortFee={cancelTargetJob.invoice_amount ?? cancelTargetJob.estimated_cost ?? 0}
          onCancelled={() => {
            setCancelTargetJob(null);
            setCancelModalOpen(false);
            fetchAwaitingDeliveryJobs();
          }}
        />
      )}

      {/* Navigation Choice Dialog */}
      <NavigationChoiceDialog
        open={navDialogOpen}
        onOpenChange={setNavDialogOpen}
        address={navDialogAddress}
        label={navDialogLabel}
      />

      {/* Job Summary Dialog */}
      <JobSummaryDialog
        job={summaryJob}
        open={!!summaryJob}
        onOpenChange={(open) => { if (!open) setSummaryJob(null); }}
      />
    </Card>
  );
};
